/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * Variant picker auto-correct for sparse variant matrices.
 *
 * When a product has options that don't form a complete matrix (e.g., Size=1 Per
 * Pallet only has Unit of Sale=Per Pallet, while Size=8-10 Per Pallet has both
 * Per Pallet and Per LB), clicking a Size that doesn't have the currently-selected
 * Unit of Sale combination would leave the form in an invalid state with an empty
 * variant id.
 *
 * This listener intercepts option changes at capture phase (before Warehouse's
 * VariantPicker processes them), uses a synchronously-available variant cache
 * to check if the new combination maps to a real variant, and if not, flips the
 * other option inputs to the first valid combination for the newly-selected value.
 * VariantPicker then reads the corrected form state via getActiveOptionValues()
 * and renders the correct variant.
 */
(function () {
  'use strict';

  // Cache of product variants by handle. Populated on page load and when the
  // product:rerender event fires (VariantPicker re-renders the form DOM but
  // the variants don't change).
  window.__variantCache = window.__variantCache || {};

  function getHandleFromUrl() {
    var match = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return match ? match[1] : null;
  }

  function primeVariantCache(handle) {
    if (!handle || window.__variantCache[handle]) return;
    // Synchronous-ish: kick off the fetch immediately so it's ready by first click
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) { window.__variantCache[handle] = data.variants || []; })
      .catch(function () { window.__variantCache[handle] = []; });
  }

  function getPickerHandle(form) {
    var picker = form && (form.closest('variant-picker') || document.querySelector('variant-picker'));
    return picker ? picker.getAttribute('handle') : getHandleFromUrl();
  }

  function findFirstAvailableVariant(variants, preferredOptions, changedPosition) {
    var changedValue = preferredOptions[changedPosition - 1];
    // 1. Exact match (available first)
    var exactAvailable = variants.find(function (v) {
      return v.available && v.options.every(function (o, i) { return o === preferredOptions[i]; });
    });
    if (exactAvailable) return exactAvailable;
    var exact = variants.find(function (v) {
      return v.options.every(function (o, i) { return o === preferredOptions[i]; });
    });
    if (exact) return exact;
    // 2. Best match on changed option value
    var candidates = variants.filter(function (v) {
      return v.options[changedPosition - 1] === changedValue;
    });
    if (candidates.length === 0) return null;
    candidates.sort(function (a, b) {
      var aScore = (a.available ? 100 : 0) + a.options.filter(function (o, i) { return o === preferredOptions[i]; }).length;
      var bScore = (b.available ? 100 : 0) + b.options.filter(function (o, i) { return o === preferredOptions[i]; }).length;
      return bScore - aScore;
    });
    return candidates[0];
  }

  function getFormOptionInputs(form) {
    var inputs = Array.from(form.querySelectorAll('input[data-option-position], select[data-option-position]'));
    var byPosition = {};
    inputs.forEach(function (input) {
      var pos = parseInt(input.getAttribute('data-option-position'), 10);
      if (!byPosition[pos]) byPosition[pos] = [];
      byPosition[pos].push(input);
    });
    return byPosition;
  }

  function labelTextFor(input) {
    var label = input.nextElementSibling;
    if (!label || label.tagName !== 'LABEL') {
      label = document.querySelector('label[for="' + input.id + '"]');
    }
    if (!label) return '';
    // Strip SVG children (cross-sold-out icon), then get clean text
    var clone = label.cloneNode(true);
    Array.from(clone.querySelectorAll('svg')).forEach(function (svg) { svg.remove(); });
    return clone.textContent.trim();
  }

  function getCurrentOptionValues(byPosition) {
    var positions = Object.keys(byPosition).map(Number).sort(function (a, b) { return a - b; });
    return positions.map(function (pos) {
      var inputs = byPosition[pos];
      var active = inputs.find(function (i) {
        if (i.tagName === 'SELECT') return true;
        if (i.type === 'hidden') return true;
        return i.checked;
      });
      if (!active) return null;
      if (active.tagName === 'SELECT') {
        var opt = active.options[active.selectedIndex];
        return opt ? opt.textContent.trim() : null;
      }
      return labelTextFor(active);
    });
  }

  function findInputForValue(inputs, valueName) {
    return inputs.find(function (i) {
      if (i.tagName === 'SELECT') return false;
      return labelTextFor(i) === valueName;
    });
  }

  // Prime the cache on page load and whenever VariantPicker re-renders the form
  document.addEventListener('DOMContentLoaded', function () {
    primeVariantCache(getHandleFromUrl());
  });
  document.addEventListener('product:rerender', function () {
    primeVariantCache(getHandleFromUrl());
  });
  // Also prime eagerly right now in case DOMContentLoaded already fired
  primeVariantCache(getHandleFromUrl());

  // Guard against recursion from our own dispatched events
  var inAutoCorrect = false;

  document.addEventListener('change', function (event) {
    if (inAutoCorrect) return;
    var target = event.target;
    if (!target || !target.matches) return;
    if (!target.matches('input[data-option-position][type="radio"], select[data-option-position]')) return;

    var form = target.form || target.closest('form');
    if (!form) return;
    var handle = getPickerHandle(form);
    if (!handle) return;

    var variants = window.__variantCache[handle];
    if (!variants || variants.length === 0) return; // cache not ready yet, skip (rare)

    var byPosition = getFormOptionInputs(form);
    var positions = Object.keys(byPosition).map(Number);
    if (positions.length < 2) return;

    var changedPos = parseInt(target.getAttribute('data-option-position'), 10);
    var currentValues = getCurrentOptionValues(byPosition);

    // Is the current combination a real variant?
    var match = variants.find(function (v) {
      return v.options.every(function (o, i) { return o === currentValues[i]; });
    });
    if (match) return;

    // Find the best variant matching the just-changed option value
    var bestVariant = findFirstAvailableVariant(variants, currentValues, changedPos);
    if (!bestVariant) return;

    // Flip the OTHER option inputs to match the best variant, synchronously,
    // so VariantPicker's bubble-phase listener sees the corrected form state.
    inAutoCorrect = true;
    try {
      positions.forEach(function (pos) {
        if (pos === changedPos) return;
        var desiredValue = bestVariant.options[pos - 1];
        var inputs = byPosition[pos];
        var desiredInput = findInputForValue(inputs, desiredValue);
        if (!desiredInput || desiredInput.checked) return;
        // Uncheck the previously-checked radio in this option group (same name)
        var groupName = desiredInput.name;
        inputs.forEach(function (i) {
          if (i.name === groupName && i !== desiredInput) {
            i.checked = false;
          }
        });
        desiredInput.checked = true;
      });
    } finally {
      inAutoCorrect = false;
    }
  }, true); // capture phase — runs before VariantPicker's delegated listener
})();
